// ═══════════════════════════════════════════════════════════
// DataContext — центральний стейт-менеджер додатку
// Lazy-loaded collection groups: core loads on mount,
// other groups load on demand via ensureGroups()
// ═══════════════════════════════════════════════════════════
import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { loadCollection, saveDoc, removeDoc } from '../firestoreService';
import { apiFetch } from '../services/api';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { initRealtimeSync } from '../services/realtimeSync';
import { mergeDelta } from '../utils/deltaMerge';

/* ── Remote refetch guard: prevents usePersistCollection from writing back ── */
const remoteRefetchCols = { current: new Set() };

// Останній список, що для колекції прийшов із сервера (refetch або
// завантаження групи). usePersistCollection ніколи не зберігає запис, який
// дослівно збігається з цією серверною копією: він не наш, а чужий, і
// пересилати його — відлуння. 01.10 склади й товари Армстронга
// пересохранялись із КОЖНОЇ відкритої вкладки (до 8 разів на товар, кожен
// раз із новим номером документа і записом «створення» від чужого імені).
// Прапорець remoteRefetchCols тут ненадійний: гілки refetch знімають його
// у finally одразу після setX, а ефект React запускає пізніше.
const lastRemoteData = new Map();
function setRemote(colName, setter, next) {
  lastRemoteData.set(colName, next);
  setter(next);
}
const idOf = (item) => String(item?.id ?? item?._fbId ?? '');

/* ── Defaults ── */
const DEFAULT_LOCATIONS = [
  { id: 1, name: 'Основна', address: '', phone: '', color: '#5F9598' },
];
const DEFAULT_CATEGORIES = [
  { id: 1, name: 'VIP',              bgColor: '#fef9c3', textColor: '#854d0e', borderColor: '#fde047', icon: '⭐' },
  { id: 2, name: 'Постійний клієнт', bgColor: '#dbeafe', textColor: '#1e40af', borderColor: '#93c5fd', icon: '💎' },
  { id: 3, name: 'Новий клієнт',     bgColor: '#dcfce7', textColor: '#166534', borderColor: '#86efac', icon: '🌱' },
  { id: 4, name: 'Корпоративний',    bgColor: '#ede9fe', textColor: '#5b21b6', borderColor: '#c4b5fd', icon: '🏢' },
  { id: 5, name: 'Співробітник',     bgColor: '#fce7f3', textColor: '#9d174d', borderColor: '#f9a8d4', icon: '👔' },
];
const DEFAULT_IMPORTANCE = [
  { id: 1, name: 'Висока',  color: '#ef4444' },
  { id: 2, name: 'Середня', color: '#f59e0b' },
  { id: 3, name: 'Низька',  color: '#22c55e' },
];

/* ── Windowed loading: only fetch recent data for large time-series collections ── */
const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = {
  // appointments: now loaded from SQL via /api/analytics/appointments-index
  // orders: now loaded from SQL via /api/analytics/orders-index
  // transactions: now loaded from SQL via /api/analytics/transactions-index
  //
  // Kept empty for clarity — primary reads are SQL, no windowing needed.
  // The Firestore fallback paths still call loadCollection without a
  // window (full history), acceptable since fallback is rare.
};
function getWindowOpts(colName) {
  const days = WINDOW_DAYS[colName];
  if (!days) return {};
  return { createdAfter: Date.now() - days * DAY_MS };
}

/* ── Diff-based Firestore persistence hook ── */
const readOnlyRef = { current: false };
const pendingSaves = { current: 0 };

// Track pending saves per item so DELETE waits for POST to finish
const pendingOps = new Map(); // key: "colName:id" → Promise

async function saveWithRetry(colName, item) {
  try {
    await saveDoc(colName, item);
  } catch (err1) {
    // Retry once after 2s
    await new Promise(r => setTimeout(r, 2000));
    await saveDoc(colName, item); // throws if still fails
  }
}

async function removeWithRetry(colName, id) {
  // Wait for any pending save of this item to finish first
  const key = `${colName}:${id}`;
  const pending = pendingOps.get(key);
  if (pending) {
    try { await pending; } catch { /* ignore — we're deleting anyway */ }
  }
  try {
    await removeDoc(colName, id);
  } catch (err1) {
    await new Promise(r => setTimeout(r, 2000));
    await removeDoc(colName, id);
  }
}

function tracked(asyncFn, opKey) {
  pendingSaves.current++;
  const promise = asyncFn().finally(() => {
    pendingSaves.current--;
    if (opKey) pendingOps.delete(opKey);
  });
  if (opKey) pendingOps.set(opKey, promise);
  return promise;
}

// Collections where persist should NEVER auto-delete documents.
// Deletes for these collections must go through explicit API calls (UI delete button).
// This prevents accidental data loss from stale/filtered state being diffed.
const NO_PERSIST_DELETE = new Set([
  'warehouses', 'locations', 'staff', 'services', 'service_groups',
  'product_categories', 'suppliers', 'salary_schemes', 'roles',
  // Time-series: never auto-delete via persist. These will switch to
  // windowed loading (last N days), so old items "disappearing" from
  // the local array is normal — not a signal to delete from Firestore.
  // Explicit deletes for these go through API calls directly.
  'appointments', 'transactions', 'stock_movements', 'orders',
  // Salary history: saves go via direct API call, old records lack `id` field
  // which confuses persist's diff logic.
  'salary_history',
]);

// Collections that are SQL-canonical AND covered by strict dual-write
// API helpers everywhere they mutate. For these, usePersistCollection
// is fully redundant — every write already lands in Firestore + SQL
// through the API path, so the persist diff has nothing useful to add
// and only opens a refetch race window. The hook short-circuits on
// these so it never triggers a second saveDoc.
//
// Add a collection here only after auditing that EVERY local state
// mutation is preceded by an awaited API call (apiSaveX/apiUpdateX/
// apiDeleteX) or a fire-and-forget API write that owns the canonical
// state. Audited members:
//   • orders — Stage 1 (2026-04-25): all 9 mutation sites in
//     OrdersPage route through apiSaveOrder/apiUpdateOrder.
//   • appointments — Stage 3 (2026-04-26): all 6 mutation sites in
//     CalendarPage await saveAppointment/updateAppointment/
//     deleteAppointment before onAppointmentsChange.
//   • transactions — Stage 3 (2026-04-26): every write is a
//     fire-and-forget saveTransactions(...).catch() across
//     CalendarPage / CustomerModal / LoyaltyPage / OrdersPage /
//     QuickSalePage; no local state mutation relies on persist.
//   • locations — Stage 4 (2026-05-13): both mutation sites in
//     LocationsSettingsPage (handleSave / confirmDelete) await
//     saveLocation / deleteLocation before onLocationsChange. Skipping
//     persist also closes the silent-loss bug where an await between
//     setLocations(boot) and coreLoaded=true let initialLoadDone gate
//     eat the user's first edit.
const NO_PERSIST_AUTOWRITE = new Set([
  'orders',
  'appointments',
  'transactions',
  'locations',
]);

function usePersistCollection(colName, data, loadedRef, toast) {
  const prevRef = useRef();
  const initialLoadDone = useRef(false);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = data;
    if (!loadedRef.current || prev === undefined) {
      console.log(`[persist:${colName}] skip: loaded=${loadedRef.current} prev=${prev === undefined ? 'undef' : prev.length}`);
      return;
    }
    if (readOnlyRef.current) {
      console.log(`[persist:${colName}] skip: readOnly`);
      return;
    }
    // Серверна копія (див. lastRemoteData): записи, що збігаються з нею
    // дослівно, не наші — їх не зберігаємо; відсутні в ній — уже видалені
    // на сервері, їх не видаляємо вдруге.
    const remote = lastRemoteData.get(colName);
    const remoteJson = new Map();
    if (remote) for (const item of remote) { const id = idOf(item); if (id) remoteJson.set(id, JSON.stringify(item)); }
    const isRemoteCopy = (id, item) => remoteJson.has(id) && remoteJson.get(id) === JSON.stringify(item);
    if (remoteRefetchCols.current.has(colName)) {
      remoteRefetchCols.current.delete(colName);
      // Refetch is wiping local state — but if the user added a NEW item
      // locally during the refetch window, that addition is unambiguously
      // a local action (not coming from the server). Persisting it here
      // prevents a race where: user clicks "Add" → state has new item →
      // persist sees remoteRefetch flag → skips → refetch result replaces
      // state → new item gone forever (and NO_PERSIST_DELETE prevents the
      // followup "deletion" cleanup, so it dies silently). Updates and
      // deletes during refetch are still skipped because they can conflict
      // with server data the refetch is bringing in.
      const prevIds = new Set();
      for (const item of prev) {
        const id = String(item.id ?? item._fbId ?? '');
        if (id) prevIds.add(id);
      }
      const additions = [];
      for (const item of data) {
        const id = String(item.id ?? item._fbId ?? '');
        if (id && !prevIds.has(id) && !isRemoteCopy(id, item)) additions.push(item);
      }
      if (additions.length === 0) {
        console.log(`[persist:${colName}] skip: remoteRefetch (no local additions)`);
        return;
      }
      console.log(`[persist:${colName}] remoteRefetch active — saving ${additions.length} local addition(s) anyway`);
      for (const item of additions) {
        const id = String(item.id ?? item._fbId ?? '');
        tracked(() => saveWithRetry(colName, item), `${colName}:${id}`).then(() => {
          console.log(`[persist:${colName}] OK (addition during refetch) id=${id}`);
        }).catch(err => {
          console.error(`Firebase save error (${colName}):`, err);
          if (toast) {
            if (err.message && err.message.includes('plan_limit')) {
              toast.error(`Ліміт плану: оновіть тарифний план для додавання нових записів (${colName}).`);
            } else {
              toast.error(`Помилка збереження (${colName}). Перезавантажте сторінку.`);
            }
          }
        });
      }
      return;
    }
    if (NO_PERSIST_AUTOWRITE.has(colName)) {
      // SQL-canonical collection — every mutation is already
      // dual-written through API helpers. Skip diff entirely so
      // we don't race the SQL refetch path.
      return;
    }
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      console.log(`[persist:${colName}] skip: initialLoad (prev=${prev.length} → data=${data.length})`);
      return;
    }
    // Стан — рівно той масив, що прийшов із сервера: зберігати нічого.
    if (remote !== undefined && data === remote) {
      console.log(`[persist:${colName}] skip: server data as-is`);
      return;
    }

    const prevMap = new Map();
    for (const item of prev) {
      const id = String(item.id ?? item._fbId ?? '');
      if (id) prevMap.set(id, item);
    }
    const nextMap = new Map();
    for (const item of data) {
      const id = String(item.id ?? item._fbId ?? '');
      if (id) nextMap.set(id, item);
    }

    let saves = 0, deletes = 0, echoes = 0;
    for (const [id, item] of nextMap) {
      const old = prevMap.get(id);
      if (!old || JSON.stringify(old) !== JSON.stringify(item)) {
        if (isRemoteCopy(id, item)) { echoes++; continue; }   // чужий запис із сервера
        saves++;
        console.log(`[persist:${colName}] SAVE id=${id}`, item.name || '');
        tracked(() => saveWithRetry(colName, item), `${colName}:${id}`).then(() => {
          console.log(`[persist:${colName}] OK id=${id}`);
        }).catch(err => {
          console.error(`Firebase save error (${colName}):`, err);
          if (toast) {
            if (err.message && err.message.includes('plan_limit')) {
              toast.error(`Ліміт плану: оновіть тарифний план для додавання нових записів (${colName}).`);
            } else {
              toast.error(`Помилка збереження (${colName}). Перезавантажте сторінку.`);
            }
          }
        });
      }
    }
    // Зникле локально, але вже відсутнє й на сервері — видалив хтось інший.
    const disappeared = [...prevMap.keys()].filter(id => !nextMap.has(id) && (!remote || remoteJson.has(id)));
    if (echoes) console.log(`[persist:${colName}] ${echoes} server echo(es) skipped`);
    if (disappeared.length > 0) {
      if (NO_PERSIST_DELETE.has(colName)) {
        // Critical collections: never auto-delete, only explicit API calls
        console.log(`[persist:${colName}] skip auto-delete (protected): ${disappeared.length} items`);
      } else if (disappeared.length >= prevMap.size * 0.1 && disappeared.length > 3) {
        // Safety: if 10%+ AND more than 3 items disappeared at once, skip deletes.
        // The absolute minimum (>3) prevents false positives on small collections
        // like salary_history where replacing 1 of 4 records = 25%. (Threshold raised from
        // 50% to 10% to protect windowed/filtered data loading.)
        console.error(`[persist:${colName}] BLOCKED bulk delete: ${disappeared.length}/${prevMap.size} items disappeared — likely windowed load or bug`);
      } else {
        for (const id of disappeared) {
          deletes++;
          console.log(`[persist:${colName}] DELETE id=${id}`);
          tracked(() => removeWithRetry(colName, id), `${colName}:${id}`).catch(err => {
            console.error(`Firebase delete error (${colName}):`, err);
            if (toast) toast.error(`Помилка видалення (${colName}). Перезавантажте сторінку.`);
          });
        }
      }
    }
    if (saves === 0 && deletes === 0) {
      console.log(`[persist:${colName}] no changes (prev=${prevMap.size} next=${nextMap.size})`);
    }
  }, [colName, data, loadedRef, toast]);
}

/* ── beforeunload: warn if pending saves ── */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (pendingSaves.current > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}

/* ── Context ── */
const DataContext = createContext();

export function DataProvider({ children, readOnly = false, skipLoad = false }) {
  readOnlyRef.current = readOnly;
  const toast = useToast();
  const { tenantId, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);

  /* ── Per-group loaded refs (for persistence safety) ── */
  const coreLoaded = useRef(false);
  const calendarLoaded = useRef(false);
  const financeLoaded = useRef(false);
  const inventoryLoaded = useRef(false);
  const salaryLoaded = useRef(false);
  const ordersLoaded = useRef(false);
  const productionLoaded = useRef(false);
  const documentsLoaded = useRef(false);
  const loyaltyLoaded = useRef(false);

  /* ── History window (days) per collection ──
   * Default 90d on hot paths (Calendar / Dashboard / POS) to cap payload
   * size as tenants accumulate history. Pages that need longer history
   * (CustomerModal lifetime record, LoyaltyPage tier calc, OrdersPage
   * period picker) call ensureHistoryWindow(collection, days|'all') on
   * mount or on filter change. Ref-based so reads/refetches always see
   * the latest value without React re-render synchronization issues. */
  const historyWindowDaysRef = useRef({
    appointments: 30,
    transactions: 30,
    orders: 30,
  });
  // Companion state — exposed via context so consumers can read current
  // windows and re-render when they change (e.g. debug UI, period pickers).
  const [historyWindowDays, setHistoryWindowDays] = useState({
    appointments: 30,
    transactions: 30,
    orders: 30,
  });

  // Build query string for an endpoint based on current window.
  // 'all' / null → no filter (backend returns full history).
  // number → ?windowDays=N
  const windowQuery = (collection) => {
    const w = historyWindowDaysRef.current[collection];
    if (!w || w === 'all') return '';
    return `?windowDays=${w}`;
  };

  // ─── Delta-sync cursors (Phase 2) ──────────────────────────────
  // Per-collection updated_at high-water mark from the last fetch.
  // 0 means "no valid cursor" → next fetch runs in full mode and
  // captures the cursor from the response. Cleared on tab return
  // after >30min hidden (visibility safeguard) or on truncated delta.
  const cursorRef = useRef({
    appointments: 0,
    transactions: 0,
    orders: 0,
  });

  // mergeDelta extracted to ../utils/deltaMerge.js so it can be unit-tested
  // in isolation. See that file for behaviour spec.

  /* Track which groups are loaded (for UI) */
  const [groupsReady, setGroupsReady] = useState({
    core: false, calendar: false, finance: false, inventory: false,
    salary: false, orders: false, production: false, documents: false, loyalty: false,
  });
  // Prevent double-loading
  const groupLoading = useRef(new Set());

  /* ── Real-time sync: lock open modals from refetch ── */
  const lockedCollections = useRef(new Set());
  const deferredRefetches = useRef(new Set());
  const syncUnsubRef = useRef(null);

  /* ── Business type ── */
  const [businessType, setBusinessType] = useState(null);
  const [businessTypeLoaded, setBusinessTypeLoaded] = useState(false);

  /* ── Plan modules (which sidebar items are available) ── */
  const [planModules, setPlanModules] = useState(null); // null = not loaded yet, {} = all allowed

  /* ── Core group state ── */
  const [locations, setLocations] = useState(DEFAULT_LOCATIONS);
  const [activeLocationId, setActiveLocationId] = useState(() => {
    const saved = localStorage.getItem('activeLocationId');
    return saved ? Number(saved) : 1;
  });
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [importanceLevels, setImportanceLevels] = useState(DEFAULT_IMPORTANCE);
  // customers is NOT in global state — on Large tenants it can be
  // tens of thousands. Consumers that need the list self-fetch via
  // /api/analytics/customers-index (full-list mode) or the paginated /
  // search / by-ids / by-phones endpoints. Writes go through
  // saveCustomer / deleteCustomer / updateCustomer in utils/customerApi.
  const [staff, setStaff] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [tariffPlans, setTariffPlans] = useState([]);
  /* ── Chrome counters (sidebar badge, notification bell) ──
   * Delivered by /api/analytics/bootstrap-core rather than fetched on
   * their own: docs/TZ_TASKS.md §9 forbids adding a request to the mount
   * window, where the Neon permit throttle already bites. Consumers may
   * adjust them locally after an action instead of refetching. */
  const [tasksOverdue, setTasksOverdue] = useState(0);
  const [tasksBlockedByMe, setTasksBlockedByMe] = useState(0);
  const [notificationsUnread, setNotificationsUnread] = useState(0);

  /* ── Calendar group ── */
  const [appointments, setAppointments] = useState([]);
  const [scheduleTemplates, setScheduleTemplates] = useState([]);

  /* ── Finance group ── */
  const [transactions, setTransactions] = useState([]);
  // cashShifts is NOT in global state — CashRegisterPage self-fetches
  // its location's shifts on mount via /api/analytics/cash-shifts-index
  // ?locationId=X. Writes go through saveDoc('cash_shifts', ...).
  const [fixedExpenses, setFixedExpenses] = useState([]);

  /* ── Inventory group ── */
  const [productCategories, setProductCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stock, setStock] = useState([]);
  // stockMovements is NOT in global state — on Large tenants it grows
  // unbounded. Consumers that need the slice fetch it themselves:
  //   - InventoryMovementsPage / InventoryPurchasesPage: full slice via
  //     /api/analytics/report-data on mount
  //   - POS/orders/appointments (FIFO peek, reversal search): narrow
  //     server endpoints (/api/stock/fifo-peek, /api/stock/movements-by-key,
  //     /api/analytics/pos-sales-cancel-status)
  // Derived `stock` is still global because it's a single SUM per
  // (product, warehouse) — bounded by catalog size, not history.
  const [suppliers, setSuppliers] = useState([]);

  /* ── Salary group ── */
  const [salarySchemes, setSalarySchemes] = useState([]);
  // salaryHistory is NOT in global state — consumers (SalaryCalcPage,
  // BusinessMonitorPage, FinancialResultPage) self-fetch on mount via
  // /api/analytics/salary-history-index. Writes go through /api/collection.

  /* ── Orders group ── */
  const [orders, setOrders] = useState([]);

  /* ── Production group ── */
  const [techCards, setTechCards] = useState([]);
  // productions is NOT in global state — ProductionPage self-fetches
  // (filtered by activeLocationId) on mount. Writes go via saveDoc
  // directly from executeProduce / cancelProduction handlers.

  /* ── Documents group ── */
  // customerDocuments is NOT in global state — docs are customer-scoped
  // and fetched by phone (CustomerModal on open, AppointmentModal on
  // clientPhone change) via /api/analytics/customer-documents-index
  // ?customerPhone=X. Writes go through saveDoc('customer_documents').

  /* ── Loyalty group ── */
  // loyaltyTransactions is intentionally NOT in the global state: on Large
  // tenants it grows unbounded. All writes go through server endpoints
  // (/api/loyalty/earn, /reverse, /process-birthday-bonuses) which update
  // Firestore + SQL + bump _meta/sync. Consumers that need per-customer
  // history fetch /api/analytics/loyalty-customer-ledger directly; the
  // LoyaltyPage Points > History tab uses /loyalty-transactions-search.
  // Balances (loyaltyBalances) are server-aggregated SUM — still loaded
  // globally because they're bounded (one row per customer).
  const [loyaltyPrograms, setLoyaltyPrograms] = useState([]);
  const [loyaltyBalances, setLoyaltyBalances] = useState([]);
  const [coupons, setCoupons] = useState([]);
  // couponUsages NOT in global state — LoyaltyPage CouponsTab self-
  // fetches via /api/analytics/coupon-usages-index. Writes go via
  // saveDoc('coupon_usages', ...) directly from POS / Orders / Calendar.
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  // customerSubscriptions NOT in global state — AppointmentModal +
  // CustomerModal self-fetch by customerId; LoyaltyPage SubscriptionsTab
  // self-fetches the full list. Writes go via saveDoc('customer_subscriptions')
  // directly from those pages.

  /* ── Unread messages ── */
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  /* Helper: stamp locationId on records that don't have one */
  const getStampLoc = useCallback(() => {
    const firstLocId = locations[0]?.id || 1;
    return arr => arr.map(item => item.locationId ? item : { ...item, locationId: firstLocId });
  }, [locations]);

  /* ── Real-time refetch ── */
  const refetchCollectionRef = useRef(null);

  const refetchCollection = useCallback(async (colName) => {
    if (lockedCollections.current.has(colName)) {
      deferredRefetches.current.add(colName);
      return;
    }
    // Wait for any pending local writes to settle before refetching.
    // Otherwise Firestore query index may not yet include the just-written doc,
    // causing refetch to drop the local save and UI to "lose" the record.
    let waitMs = 0;
    while (pendingSaves.current > 0 && waitMs < 3000) {
      await new Promise(r => setTimeout(r, 100));
      waitMs += 100;
    }
    // Small buffer for Firestore query index propagation after writes complete
    if (waitMs > 0) await new Promise(r => setTimeout(r, 200));

    const stampFn = getStampLoc();
    const SYNC_MAP = {
      appointments:           { setter: setAppointments,          loadedRef: calendarLoaded,  stamp: true },
      // customers intentionally absent — consumers self-fetch (Phase 5c)
      staff:                  { setter: setStaff,                 loadedRef: coreLoaded },
      services:               { setter: setServices,              loadedRef: coreLoaded },
      service_groups:         { setter: setServiceGroups,         loadedRef: coreLoaded },
      transactions:           { setter: setTransactions,          loadedRef: financeLoaded,   stamp: true },
      orders:                 { setter: setOrders,                loadedRef: ordersLoaded,    stamp: true },
      // customer_subscriptions intentionally absent — Phase A6 self-fetch.
      loyalty_balances:       { setter: setLoyaltyBalances,       loadedRef: loyaltyLoaded },
      products:               { setter: setProducts,              loadedRef: inventoryLoaded },
      stock:                  { setter: setStock,                 loadedRef: inventoryLoaded },
    };
    // stock_movements is not in DataContext anymore — consumers fetch
    // their own slice (InventoryMovementsPage, InventoryPurchasesPage on
    // mount; hot paths via /api/stock/fifo-peek etc). No refetch branch
    // needed; the derived `stock` aggregate below still refreshes on
    // stock_movements changes.


    // customers is not in DataContext anymore — pages self-fetch via
    // /api/analytics/customers-index on demand. No tenant-wide refetch
    // needed here; individual pages re-query their own scope when
    // writes happen (clients view via bumpListReload; modals on remount).

    // transactions: delta-sync (Phase 2). Append-only on the SQL side
    // so deltas are essentially "new rows since cursor", but the protocol
    // is uniform with appointments / orders.
    if (colName === 'transactions') {
      if (!financeLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const cursor = cursorRef.current.transactions;
        const wq = windowQuery('transactions');
        const url = cursor > 0
          ? `/api/analytics/transactions-index${wq}${wq ? '&' : '?'}since=${cursor}`
          : `/api/analytics/transactions-index${wq}`;
        const res = await apiFetch(url);
        if (res.truncated) {
          cursorRef.current.transactions = 0;
          remoteRefetchCols.current.delete(colName);
          return refetchCollectionRef.current?.(colName);
        }
        if (res.delta) {
          setTransactions(prev => mergeDelta(prev, res.transactions || []));
        } else {
          setTransactions(res.transactions || []);
        }
        if (typeof res.cursor === 'number') cursorRef.current.transactions = res.cursor;
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // products: refetch from SQL instead of Firestore
    if (colName === 'products') {
      if (!inventoryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/products-index');
        setRemote('products', setProducts, res.products || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // stock: computed from stock_movements aggregate (no standalone storage)
    if (colName === 'stock') {
      if (!inventoryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/stock-index');
        setStock(res.stock || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // loyalty_transactions is no longer loaded tenant-wide — pages that
    // need a slice fetch /api/analytics/loyalty-customer-ledger (per customer)
    // or /loyalty-transactions-search (LoyaltyPage history). Realtime sync
    // bumps loyalty_transactions only to trigger loyalty_balances refetch,
    // which still comes from the server aggregate below.

    // loyalty_balances refetch is now a no-op — the tenant-wide SUM
    // aggregate was the largest unbounded scan in the system. Pages
    // that need balance data fetch purpose-built slices on their own:
    //   • LoyaltyPage → /loyalty-balance-stats + /loyalty-balances-top
    //   • AppointmentModal / OrdersPage / CustomerModal →
    //     /loyalty-balance?customerId=X (indexed lookup)
    // The _meta/sync.loyalty_balances bump still fires on transaction
    // writes; pages that want live updates subscribe via subscribeSync.
    if (colName === 'loyalty_balances') return;

    // coupon_usages is not in DataContext anymore — LoyaltyPage
    // CouponsTab self-fetches its own copy on mount.

    // customer_subscriptions is not in DataContext anymore (Phase A6).
    // AppointmentModal/CustomerModal self-fetch by customerId; LoyaltyPage
    // self-fetches the full list. No tenant-wide refetch needed.

    // cash_shifts is not in DataContext anymore — CashRegisterPage
    // self-fetches by locationId on mount. No tenant-wide refetch here.

    // fixed_expenses: financial records now SQL-backed
    if (colName === 'fixed_expenses') {
      if (!financeLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/fixed-expenses-index');
        setRemote('fixed_expenses', setFixedExpenses, res.expenses || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // Core config (Bundle C Stage 1) — SQL-backed with DEFAULTs on empty.
    if (colName === 'categories') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/categories-index');
        const list = res.categories || [];
        setRemote('categories', setCategories, list.length ? list : DEFAULT_CATEGORIES);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'importance') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/importance-index');
        const list = res.levels || [];
        setRemote('importance', setImportanceLevels, list.length ? list : DEFAULT_IMPORTANCE);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'schedule_templates') {
      if (!calendarLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/schedule-templates-index');
        setRemote('schedule_templates', setScheduleTemplates, res.templates || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'tariff_plans') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/tariff-plans-index');
        setRemote('tariff_plans', setTariffPlans, res.plans || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'locations') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/locations-index');
        const list = res.locations || [];
        // MERGE rather than replace — if the user added a new location
        // during the refetch window, that local-only row would otherwise
        // get wiped out by the server's view (which doesn't yet know
        // about it). The persist hook's complementary fix saves the
        // local addition; this fix keeps it visible in the UI until
        // the save completes and a subsequent sync bump round-trips it.
        // Same fallback chain as initial load so sidebar / stampLoc keep
        // working if a superadmin deletes all locations from a tenant.
        setLocations(prev => {
          const serverIds = new Set(list.map(l => String(l.id)));
          const localOnly = (prev || []).filter(l => !serverIds.has(String(l.id)));
          const merged = [...list, ...localOnly];
          return merged.length ? merged : DEFAULT_LOCATIONS;
        });
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // Production module (Bundle E) — SQL-backed.
    if (colName === 'tech_cards') {
      if (!productionLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/tech-cards-index');
        setRemote('tech_cards', setTechCards, res.techCards || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    // productions is not in DataContext anymore — ProductionPage
    // self-fetches by locationId on mount. No tenant-wide refetch here.

    // customer_documents is not in DataContext anymore — CustomerModal
    // and AppointmentModal self-fetch their own slice (by customerPhone).
    // No tenant-wide refetch needed here.

    // salary_schemes: SQL-backed (Bundle D)
    if (colName === 'salary_schemes') {
      if (!salaryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/salary-schemes-index');
        setRemote('salary_schemes', setSalarySchemes, res.schemes || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // salary_history is not in DataContext anymore — consumers
    // (SalaryCalcPage / BusinessMonitorPage / FinancialResultPage)
    // self-fetch on mount. No tenant-wide refetch needed here.

    // appointments: delta-sync (Phase 2). Falls back to full refetch when
    // no cursor (first sync after init) or server signals truncated.
    if (colName === 'appointments') {
      if (!calendarLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const cursor = cursorRef.current.appointments;
        const wq = windowQuery('appointments');
        const url = cursor > 0
          ? `/api/analytics/appointments-index${wq}${wq ? '&' : '?'}since=${cursor}`
          : `/api/analytics/appointments-index${wq}`;
        const res = await apiFetch(url);
        if (res.truncated) {
          // Delta exceeded the cap (5000 rows since cursor) — drop cursor
          // and re-enter via the same handler to refetch in full mode.
          cursorRef.current.appointments = 0;
          remoteRefetchCols.current.delete(colName);
          return refetchCollectionRef.current?.(colName);
        }
        if (res.delta) {
          setAppointments(prev => stampFn(mergeDelta(prev, res.appointments || [])));
        } else {
          setAppointments(stampFn(res.appointments || []));
        }
        if (typeof res.cursor === 'number') cursorRef.current.appointments = res.cursor;
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // orders: delta-sync (Phase 2). Same shape as appointments.
    if (colName === 'orders') {
      if (!ordersLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const cursor = cursorRef.current.orders;
        const wq = windowQuery('orders');
        const url = cursor > 0
          ? `/api/analytics/orders-index${wq}${wq ? '&' : '?'}since=${cursor}`
          : `/api/analytics/orders-index${wq}`;
        const res = await apiFetch(url);
        if (res.truncated) {
          cursorRef.current.orders = 0;
          remoteRefetchCols.current.delete(colName);
          return refetchCollectionRef.current?.(colName);
        }
        if (res.delta) {
          setOrders(prev => stampFn(mergeDelta(prev, res.orders || [])));
        } else {
          setOrders(stampFn(res.orders || []));
        }
        if (typeof res.cursor === 'number') cursorRef.current.orders = res.cursor;
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // staff / services / service_groups — all in core group, now SQL-backed.
    if (colName === 'staff') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/staff-index');
        setRemote('staff', setStaff, res.staff || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'services') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/services-index');
        setRemote('services', setServices, res.services || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'service_groups') {
      if (!coreLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/service-groups-index');
        setRemote('service_groups', setServiceGroups, res.groups || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // Inventory references (Bundle A) — all now SQL-backed.
    if (colName === 'product_categories') {
      if (!inventoryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/product-categories-index');
        setRemote('product_categories', setProductCategories, res.categories || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'warehouses') {
      if (!inventoryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/warehouses-index');
        const firstLocId = locations[0]?.id || 1;
        const list = (res.warehouses || []).map(w => w.locationId ? w : { ...w, locationId: firstLocId });
        setRemote('warehouses', setWarehouses, list);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'suppliers') {
      if (!inventoryLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/suppliers-index');
        setRemote('suppliers', setSuppliers, res.suppliers || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    // Loyalty master lists (Bundle B) — all now SQL-backed.
    if (colName === 'loyalty_programs') {
      if (!loyaltyLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/loyalty-programs-index');
        setRemote('loyalty_programs', setLoyaltyPrograms, res.programs || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'coupons') {
      if (!loyaltyLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/coupons-index');
        setRemote('coupons', setCoupons, res.coupons || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }
    if (colName === 'subscription_plans') {
      if (!loyaltyLoaded.current) return;
      try {
        remoteRefetchCols.current.add(colName);
        const res = await apiFetch('/api/analytics/subscription-plans-index');
        setRemote('subscription_plans', setSubscriptionPlans, res.plans || []);
      } catch {} finally { remoteRefetchCols.current.delete(colName); }
      return;
    }

    const entry = SYNC_MAP[colName];
    if (!entry || !entry.loadedRef.current) return; // group not yet loaded — skip
    try {
      remoteRefetchCols.current.add(colName);
      const data = await loadCollection(colName, getWindowOpts(colName));
      setRemote(colName, entry.setter, entry.stamp ? stampFn(data) : data);
    } catch (err) {
      remoteRefetchCols.current.delete(colName);
      console.warn(`[realtimeSync] refetch failed for ${colName}:`, err);
    }
  }, [getStampLoc]);

  // Keep a stable ref so the sync listener doesn't recreate on every render
  refetchCollectionRef.current = refetchCollection;

  /**
   * Widen the history window for a SQL-backed collection and refetch.
   * No-op if the current window already covers the requested size.
   *
   *   collection: 'appointments' | 'transactions' | 'orders'
   *   days: number of days, or 'all' for full history
   *
   * Pages that need lifetime/long history call this on mount:
   *   useEffect(() => { ensureHistoryWindow('transactions', 'all'); }, []);
   *
   * Subsequent realtime refetches automatically use the new widest window
   * because windowQuery() reads from historyWindowDaysRef.
   */
  // Debounce timers per collection — when the user navigates rapidly
  // (e.g. clicking through Calendar dates) ensureHistoryWindow is
  // called repeatedly with growing day counts. Without coalescing,
  // every click fires its own refetch chain. 300ms after the last
  // call, refetch once with whatever the widest window is at that
  // point (already in historyWindowDaysRef).
  const ensureHistoryWindowTimers = useRef({
    appointments: null,
    transactions: null,
    orders: null,
  });

  const ensureHistoryWindow = useCallback((collection, days) => {
    if (!['appointments', 'transactions', 'orders'].includes(collection)) return;
    const current = historyWindowDaysRef.current[collection];
    // Already at maximum — nothing to do.
    if (current === 'all') return;
    // Already wide enough — nothing to do.
    if (days !== 'all' && typeof days === 'number' && current >= days) return;

    // Update ref/state immediately so subsequent windowQuery() reads
    // (and the eventually-fired refetch) see the widest requested value.
    historyWindowDaysRef.current = { ...historyWindowDaysRef.current, [collection]: days };
    setHistoryWindowDays(prev => ({ ...prev, [collection]: days }));

    // Drop cursor — widening the window pulls in older rows whose
    // updated_at is below the current cursor; a delta-mode refetch
    // wouldn't return them. Setting cursor to 0 forces full mode on
    // the next refetch, which then captures a fresh cursor.
    cursorRef.current = { ...cursorRef.current, [collection]: 0 };

    // Debounce the actual refetch.
    const timers = ensureHistoryWindowTimers.current;
    if (timers[collection]) clearTimeout(timers[collection]);
    timers[collection] = setTimeout(() => {
      timers[collection] = null;
      // Trigger refetch via the existing refetchCollection path so all the
      // stamping, remoteRefetchCols guard, and _meta/sync wiring stay consistent.
      Promise.resolve(refetchCollectionRef.current?.(collection)).catch(err => {
        console.warn(`[DataContext] ensureHistoryWindow refetch failed for ${collection}:`, err);
      });
    }, 300);
  }, []);

  const lockCollection = useCallback((colName) => {
    lockedCollections.current.add(colName);
  }, []);

  const unlockCollection = useCallback(async (colName) => {
    lockedCollections.current.delete(colName);
    if (deferredRefetches.current.has(colName)) {
      deferredRefetches.current.delete(colName);
      await refetchCollectionRef.current?.(colName);
    }
  }, []);

  // External subscribers (e.g. CommunicationPage) can register handlers per
  // collection and get notified when the realtime sync doc reports changes —
  // no polling required for anything outside DataContext's own SYNC_MAP.
  const syncListenersRef = useRef(new Map());
  const subscribeSync = useCallback((col, handler) => {
    let set = syncListenersRef.current.get(col);
    if (!set) { set = new Set(); syncListenersRef.current.set(col, set); }
    set.add(handler);
    return () => { set.delete(handler); };
  }, []);

  /* ── Init real-time sync listener after core loads ── */
  useEffect(() => {
    if (!groupsReady.core || skipLoad || !tenantId) return;
    if (syncUnsubRef.current) return; // already subscribed
    syncUnsubRef.current = initRealtimeSync(tenantId, (changed) => {
      changed.forEach(col => {
        refetchCollectionRef.current?.(col);
        const handlers = syncListenersRef.current.get(col);
        if (handlers) handlers.forEach(h => { try { h(); } catch (_) { /* ignore */ } });
      });
    });
    return () => {
      if (syncUnsubRef.current) { syncUnsubRef.current(); syncUnsubRef.current = null; }
    };
  }, [groupsReady.core, skipLoad, tenantId]);

  /* ── Delta-sync safeguard: visibility fallback ──────────────────
     If the tab was hidden for >30 minutes, the Firestore listener may
     have missed events (browser suspension, dropped websocket without
     auto-reconnect, OS sleep). On focus, drop cursors and force a full
     refetch for delta-tracked collections — guarantees fresh state
     without continuous polling. */
  useEffect(() => {
    if (!groupsReady.core || skipLoad) return;
    const STALE_THRESHOLD = 30 * 60 * 1000; // 30 min
    let hiddenAt = null;
    const onVis = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      if (!hiddenAt) return;
      const hiddenFor = Date.now() - hiddenAt;
      hiddenAt = null;
      if (hiddenFor < STALE_THRESHOLD) return;
      // Force full refetch by clearing cursors.
      cursorRef.current = { appointments: 0, transactions: 0, orders: 0 };
      ['appointments', 'transactions', 'orders'].forEach(col => {
        refetchCollectionRef.current?.(col);
      });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [groupsReady.core, skipLoad]);

  /* ── Load CORE group on mount ── */
  useEffect(() => {
    async function init() {
      // Superadmin without selected tenant — skip loading, show empty state
      if (skipLoad) {
        setBusinessTypeLoaded(true);
        coreLoaded.current = true;
        setGroupsReady(prev => ({ ...prev, core: true }));
        setLoading(false);
        return;
      }
      try {
        // Single-trip bootstrap: one serverless invocation, one Neon
        // connection, seven SQL queries in parallel. Replaces seven
        // separate `/api/analytics/*-index` fetches that each opened
        // their own TLS + Neon permit. Saves a round-trip on cold
        // login and removes a common burst-throttle trigger on Neon's
        // compute proxy. Per-collection empties still fall through to
        // the existing Firestore → DEFAULT_* chains below (a single
        // failing table yields [] for that field, not a 500 for the
        // whole call).
        const boot = await apiFetch('/api/analytics/bootstrap-core').catch(() => null);
        const sqlLocations = boot?.locations || [];
        const sqlCategories = boot?.categories || [];
        const sqlImportance = boot?.importance || [];
        const sqlStaff = boot?.staff || [];
        const sqlServiceGroups = boot?.serviceGroups || [];
        const sqlServices = boot?.services || [];
        const sqlTariffPlans = boot?.tariffPlans || [];
        // Rollout fallback to Firestore if SQL returned empty (backfill
        // not yet run for this tenant). Only fires once; after backfill
        // the branch is dead.
        let fbStaff = sqlStaff;
        if (!fbStaff.length) {
          const fsStaff = await loadCollection('staff').catch(() => []);
          if (fsStaff.length) { console.warn('[DataContext] SQL staff empty — Firestore fallback'); fbStaff = fsStaff; }
        }
        let fbServiceGroups = sqlServiceGroups;
        if (!fbServiceGroups.length) {
          const fsSG = await loadCollection('service_groups').catch(() => []);
          if (fsSG.length) { console.warn('[DataContext] SQL service_groups empty — Firestore fallback'); fbServiceGroups = fsSG; }
        }
        let fbServices = sqlServices;
        if (!fbServices.length) {
          const fsSvc = await loadCollection('services').catch(() => []);
          if (fsSvc.length) { console.warn('[DataContext] SQL services empty — Firestore fallback'); fbServices = fsSvc; }
        }

        // Resolve every core collection BEFORE any setX. Each block may
        // await loadCollection() as a Firestore fallback when SQL is
        // empty (pre-backfill or fresh tenant). Awaits between setX and
        // coreLoaded.current=true are *the* cause of the silent-loss
        // bug: React commits a setX while loaded=false, the persist
        // hook skips at gate 1, and the initialLoadDone gate then eats
        // the user's first real edit instead of the bootstrap value.
        // Fix: all awaits live up here; all setters fire in the sync
        // batch below.
        let locsResolved = sqlLocations;
        if (!locsResolved.length) {
          const fbLocations = await loadCollection('locations').catch(() => []);
          if (fbLocations.length) {
            console.warn('[DataContext] SQL locations empty — Firestore fallback');
            locsResolved = fbLocations;
          }
        }
        const locs = locsResolved.length ? locsResolved : DEFAULT_LOCATIONS;

        let catsResolved = sqlCategories;
        if (!catsResolved.length) {
          const fbCategories = await loadCollection('categories').catch(() => []);
          if (fbCategories.length) {
            console.warn('[DataContext] SQL categories empty — Firestore fallback');
            catsResolved = fbCategories;
          }
        }
        const cats = catsResolved.length ? catsResolved : DEFAULT_CATEGORIES;

        let impResolved = sqlImportance;
        if (!impResolved.length) {
          const fbImportance = await loadCollection('importance').catch(() => []);
          if (fbImportance.length) {
            console.warn('[DataContext] SQL importance empty — Firestore fallback');
            impResolved = fbImportance;
          }
        }
        const imp = impResolved.length ? impResolved : DEFAULT_IMPORTANCE;

        // Rollout fallback for tariff_plans: SQL → Firestore. No runtime
        // DEFAULT — if both are empty, the tenant simply has no custom
        // tariffs and appointments use base service prices.
        let tariffs = sqlTariffPlans;
        if (!tariffs.length) {
          const fbTariffPlans = await loadCollection('tariff_plans').catch(() => []);
          if (fbTariffPlans.length) {
            console.warn('[DataContext] SQL tariff_plans empty — Firestore fallback');
            tariffs = fbTariffPlans;
          }
        }

        // Resolution order for activeLocationId:
        //   1. localStorage (so user's manual switch persists across refreshes)
        //   2. userProfile.startingLocationId (per-user preference from Profile modal)
        //   3. locs[0] (fallback: first by id/creation order)
        // localStorage is validated against current locs so a stale id
        // (e.g. deleted location) falls through to the next option.
        const savedLocId = localStorage.getItem('activeLocationId');
        const startingPref = userProfile?.startingLocationId;
        const hasSaved = savedLocId && locs.find(l => String(l.id) === String(savedLocId));
        const hasStartingPref = startingPref && locs.find(l => String(l.id) === String(startingPref));
        const targetId = hasSaved
          ? savedLocId
          : hasStartingPref
            ? String(startingPref)
            : String(locs[0]?.id || 1);
        const needsActiveLocUpdate = String(activeLocationId) !== targetId;

        // ── Single sync commit block ──
        // All setX calls and coreLoaded.current=true MUST stay in this
        // unbroken sync block. No awaits allowed between them. React 18
        // batches every setX into one commit; persist hooks then run
        // once with loaded=true, so the initialLoadDone gate consumes
        // the bootstrap delta — not the user's first edit.
        setLocations(locs);
        if (needsActiveLocUpdate) {
          setActiveLocationId(Number.isNaN(Number(targetId)) ? targetId : Number(targetId));
          localStorage.setItem('activeLocationId', targetId);
        }
        setRemote('categories', setCategories, cats);
        setRemote('importance', setImportanceLevels, imp);
        setRemote('staff', setStaff, fbStaff);
        setRemote('service_groups', setServiceGroups, fbServiceGroups);
        setRemote('services', setServices, fbServices);
        setRemote('tariff_plans', setTariffPlans, tariffs);
        // Inside the sync block on purpose. An await between a setX and
        // coreLoaded.current = true is what caused the 2026-05-13 silent
        // loss: React commits the state while loaded is still false, the
        // persist hook skips at its first gate, and the initialLoadDone
        // gate then eats the user's first real edit instead of the
        // bootstrap value.
        setTasksOverdue(boot?.tasksOverdue || 0);
        setTasksBlockedByMe(boot?.tasksBlockedByMe || 0);
        setNotificationsUnread(boot?.notificationsUnread || 0);
        coreLoaded.current = true;
        setGroupsReady(prev => ({ ...prev, core: true }));

        try {
          const btRes = await apiFetch('/api/settings?key=business_type');
          if (btRes.data?.type) setBusinessType(btRes.data.type);
        } catch (err) {
          console.error('Failed to load business_type:', err);
        } finally {
          setBusinessTypeLoaded(true);
        }

        try {
          const pmRes = await apiFetch('/api/plan-modules');
          if (pmRes.modules) setPlanModules(pmRes.modules);
        } catch {
          setPlanModules({}); // fallback: all modules allowed
        }

      } catch (err) {
        console.error('Core load error:', err);
        setBusinessTypeLoaded(true);
      }
      setLoading(false);

      // All non-core data groups (inventory, calendar, finance, etc.) load on
      // demand via ensureGroups() when the user navigates to a page that needs
      // them. No eager prefetch — saves ~100 reads per CRM open.
    }
    init();
  }, []);

  /* ── Lazy group loaders ── */
  const loadGroupData = useCallback(async (groupName) => {
    const stampLoc = getStampLoc();

    switch (groupName) {
      case 'calendar': {
        const [sqlAppRes, sqlSched] = await Promise.all([
          // appointments from SQL with a default 30-day window — covers
          // Calendar / AppointmentModal / current-week views. Pages that
          // need longer history (CustomerModal, LoyaltyPage) call
          // ensureHistoryWindow('appointments','all') on mount and trigger
          // a refetch with the wider window. Capture cursor for delta sync.
          apiFetch(`/api/analytics/appointments-index${windowQuery('appointments')}`)
            .catch(() => ({ appointments: [] })),
          // schedule_templates now SQL-backed (Bundle C Stage 1).
          apiFetch('/api/analytics/schedule-templates-index')
            .then(r => r.templates || []).catch(() => []),
        ]);
        const sqlApp = sqlAppRes.appointments || [];
        if (typeof sqlAppRes.cursor === 'number') cursorRef.current.appointments = sqlAppRes.cursor;
        // Firestore fallback during rollout if SQL returned empty.
        let apps = sqlApp;
        if (!apps.length) {
          const fbApp = await loadCollection('appointments', getWindowOpts('appointments')).catch(() => []);
          if (fbApp.length) {
            console.warn('[DataContext] SQL appointments empty — Firestore fallback');
            apps = fbApp;
          }
        }
        let templates = sqlSched;
        if (!templates.length) {
          const fbSched = await loadCollection('schedule_templates').catch(() => []);
          if (fbSched.length) {
            console.warn('[DataContext] SQL schedule_templates empty — Firestore fallback');
            templates = fbSched;
          }
        }
        setAppointments(stampLoc(apps));
        setRemote('schedule_templates', setScheduleTemplates, templates);
        calendarLoaded.current = true;
        break;
      }
      case 'finance': {
        // cashShifts dropped from DataContext (Phase A4) — CashRegisterPage
        // self-fetches by locationId. transactions still windowed to 90d
        // by default (pages widen via ensureHistoryWindow when needed).
        const [sqlTxRes, sqlFixed] = await Promise.all([
          apiFetch(`/api/analytics/transactions-index${windowQuery('transactions')}`)
            .catch(() => ({ transactions: [] })),
          apiFetch('/api/analytics/fixed-expenses-index')
            .then(r => r.expenses || []).catch(() => []),
        ]);
        const sqlTx = sqlTxRes.transactions || [];
        if (typeof sqlTxRes.cursor === 'number') cursorRef.current.transactions = sqlTxRes.cursor;
        setTransactions(sqlTx);
        let fixed = sqlFixed;
        if (!fixed.length) {
          const fbFixed = await loadCollection('fixed_expenses').catch(() => []);
          if (fbFixed.length) { console.warn('[DataContext] SQL fixed_expenses empty — Firestore fallback'); fixed = fbFixed; }
        }
        setRemote('fixed_expenses', setFixedExpenses, fixed);
        financeLoaded.current = true;
        break;
      }
      case 'inventory': {
        // Single-trip bootstrap — one serverless invocation, one Neon
        // connection. stock_movements NOT loaded tenant-wide; Inventory
        // pages fetch their own slice on mount, hot paths use narrow
        // endpoints.
        const boot = await apiFetch('/api/analytics/bootstrap-inventory').catch(() => null);
        const sqlPC = boot?.productCategories || [];
        const sqlProd = boot?.products || [];
        const sqlWh = boot?.warehouses || [];
        const sqlStock = boot?.stock || [];
        const sqlSuppliers = boot?.suppliers || [];
        // Firestore fallback during rollout for the just-migrated master lists.
        let productCats = sqlPC;
        if (!productCats.length) {
          const fbPC = await loadCollection('product_categories').catch(() => []);
          if (fbPC.length) { console.warn('[DataContext] SQL product_categories empty — Firestore fallback'); productCats = fbPC; }
        }
        let warehousesList = sqlWh;
        if (!warehousesList.length) {
          const fbWh = await loadCollection('warehouses').catch(() => []);
          if (fbWh.length) { console.warn('[DataContext] SQL warehouses empty — Firestore fallback'); warehousesList = fbWh; }
        }
        let suppliersList = sqlSuppliers;
        if (!suppliersList.length) {
          const fbSuppliers = await loadCollection('suppliers').catch(() => []);
          if (fbSuppliers.length) { console.warn('[DataContext] SQL suppliers empty — Firestore fallback'); suppliersList = fbSuppliers; }
        }
        setRemote('product_categories', setProductCategories, productCats);
        setRemote('products', setProducts, sqlProd);
        const firstLocId = locations[0]?.id || 1;
        setRemote('warehouses', setWarehouses, warehousesList.map(w => w.locationId ? w : { ...w, locationId: firstLocId }));
        setStock(sqlStock);
        setRemote('suppliers', setSuppliers, suppliersList);
        inventoryLoaded.current = true;
        break;
      }
      case 'salary': {
        // salaryHistory dropped from DataContext (Phase A2) —
        // SalaryCalcPage / BusinessMonitor / FinancialResult self-fetch.
        let schemes = await apiFetch('/api/analytics/salary-schemes-index')
          .then(r => r.schemes || []).catch(() => []);
        // Firestore fallback during rollout for salary_schemes until backfill runs.
        if (!schemes.length) {
          const fbSchemes = await loadCollection('salary_schemes').catch(() => []);
          if (fbSchemes.length) {
            console.warn('[DataContext] SQL salary_schemes empty — Firestore fallback');
            schemes = fbSchemes;
          }
        }
        setRemote('salary_schemes', setSalarySchemes, schemes);
        salaryLoaded.current = true;
        break;
      }
      case 'orders': {
        // orders from SQL with 30-day default window. OrdersPage's date
        // filter calls ensureHistoryWindow('orders', N) to widen when user
        // picks older period.
        const ordersRes = await apiFetch(`/api/analytics/orders-index${windowQuery('orders')}`)
          .catch(() => ({ orders: [] }));
        if (typeof ordersRes.cursor === 'number') cursorRef.current.orders = ordersRes.cursor;
        let ordersList = ordersRes.orders || [];
        if (!ordersList.length) {
          const fbOrders = await loadCollection('orders', getWindowOpts('orders')).catch(() => []);
          if (fbOrders.length) {
            console.warn('[DataContext] SQL orders empty — Firestore fallback');
            ordersList = fbOrders;
          }
        }
        setOrders(stampLoc(ordersList));
        ordersLoaded.current = true;
        break;
      }
      case 'production': {
        // productions dropped from DataContext (Phase A3) — ProductionPage
        // self-fetches by locationId. tech_cards still global (bounded).
        let techCardsList = await apiFetch('/api/analytics/tech-cards-index')
          .then(r => r.techCards || []).catch(() => []);
        if (!techCardsList.length) {
          const fbTC = await loadCollection('tech_cards').catch(() => []);
          if (fbTC.length) { console.warn('[DataContext] SQL tech_cards empty — Firestore fallback'); techCardsList = fbTC; }
        }
        setRemote('tech_cards', setTechCards, techCardsList);
        productionLoaded.current = true;
        break;
      }
      case 'documents': {
        // customer_documents is no longer loaded tenant-wide here —
        // CustomerModal / AppointmentModal self-fetch by customerPhone.
        // Group flag set so ensureGroups('documents') remains a no-op.
        documentsLoaded.current = true;
        break;
      }
      case 'loyalty': {
        // Single-trip bootstrap — one serverless invocation, one Neon
        // connection. balances are a SUM over loyalty_transactions;
        // customer_subscriptions still falls back to Firestore below
        // on SQL-empty (rollout safety net until backfill runs).
        const boot = await apiFetch('/api/analytics/bootstrap-loyalty').catch(() => null);
        const sqlLP = boot?.loyaltyPrograms || [];
        // loyaltyBalances no longer in bootstrap response — see
        // /loyalty-balance-stats / /loyalty-balance / /loyalty-balances-top.
        const sqlCpn = boot?.coupons || [];
        // couponUsages dropped (Phase A5) — LoyaltyPage self-fetches.
        // customerSubscriptions dropped (Phase A6) — pages self-fetch.
        const sqlSP = boot?.subscriptionPlans || [];
        // Firestore fallback during rollout for the just-migrated master lists.
        let programs = sqlLP;
        if (!programs.length) {
          const fbLP = await loadCollection('loyalty_programs').catch(() => []);
          if (fbLP.length) { console.warn('[DataContext] SQL loyalty_programs empty — Firestore fallback'); programs = fbLP; }
        }
        let couponsList = sqlCpn;
        if (!couponsList.length) {
          const fbCpn = await loadCollection('coupons').catch(() => []);
          if (fbCpn.length) { console.warn('[DataContext] SQL coupons empty — Firestore fallback'); couponsList = fbCpn; }
        }
        let plans = sqlSP;
        if (!plans.length) {
          const fbSP = await loadCollection('subscription_plans').catch(() => []);
          if (fbSP.length) { console.warn('[DataContext] SQL subscription_plans empty — Firestore fallback'); plans = fbSP; }
        }
        setRemote('loyalty_programs', setLoyaltyPrograms, programs);
        // loyaltyBalances stays empty — pages fetch purpose-built slices.
        setRemote('coupons', setCoupons, couponsList);
        setRemote('subscription_plans', setSubscriptionPlans, plans);
        loyaltyLoaded.current = true;
        break;
      }
      default:
        break;
    }
  }, [getStampLoc, locations]);

  /**
   * Ensure one or more data groups are loaded.
   * Call from App.js before rendering a page that needs them.
   * Returns a promise that resolves when all requested groups are ready.
   */
  const ensureGroups = useCallback(async (...names) => {
    const toLoad = names.filter(n => {
      if (n === 'core') return false; // core always loaded on mount
      const refMap = {
        calendar: calendarLoaded, finance: financeLoaded,
        inventory: inventoryLoaded, salary: salaryLoaded,
        orders: ordersLoaded, production: productionLoaded,
        documents: documentsLoaded, loyalty: loyaltyLoaded,
      };
      return refMap[n] && !refMap[n].current && !groupLoading.current.has(n);
    });
    if (toLoad.length === 0) return;

    toLoad.forEach(n => groupLoading.current.add(n));

    try {
      await Promise.all(toLoad.map(n => loadGroupData(n).catch(err => {
        console.error(`Group load error (${n}):`, err);
      })));
    } finally {
      const refMap = {
        calendar: calendarLoaded, finance: financeLoaded,
        inventory: inventoryLoaded, salary: salaryLoaded,
        orders: ordersLoaded, production: productionLoaded,
        documents: documentsLoaded, loyalty: loyaltyLoaded,
      };
      toLoad.forEach(n => {
        if (refMap[n]) refMap[n].current = true;
        groupLoading.current.delete(n);
      });
      setGroupsReady(prev => {
        const next = { ...prev };
        toLoad.forEach(n => { next[n] = true; });
        return next;
      });
    }
  }, [loadGroupData]);

  /* ── Unread count: realtime via _meta/sync.messages, no polling ── */
  const loadUnread = useCallback(async () => {
    try {
      const res = await apiFetch('/api/unread-count');
      setUnreadMsgCount(res.count || 0);
    } catch { /* ignore */ }
  }, []);
  // Initial fetch on mount (and after login/tenant switch).
  useEffect(() => {
    if (!tenantId || skipLoad) return;
    loadUnread();
  }, [tenantId, skipLoad, loadUnread]);
  // Refetch whenever the realtime sync reports a messages change.
  useEffect(() => {
    return subscribeSync('messages', loadUnread);
  }, [subscribeSync, loadUnread]);

  // Лічильники задач і дзвіночка — живі. Кожна мутація задач і кожне
  // сповіщення бампають _meta/sync; досі на це ніхто не був підписаний, і
  // бейджі стояли до перезавантаження. Перечитуємо три числа одним легким
  // запитом, не bootstrap-core.
  useEffect(() => {
    if (!tenantId || skipLoad) return undefined;
    const refresh = () => {
      apiFetch('/api/notifications?limit=1&counters=1')
        .then(r => {
          if (typeof r?.tasksOverdue === 'number') setTasksOverdue(r.tasksOverdue);
          if (typeof r?.tasksBlockedByMe === 'number') setTasksBlockedByMe(r.tasksBlockedByMe);
          setNotificationsUnread(r?.unread || 0);
        })
        .catch(() => {});
    };
    const unsubTasks = subscribeSync('tasks', refresh);
    const unsubNotif = subscribeSync('notifications', refresh);
    return () => { unsubTasks(); unsubNotif(); };
  }, [subscribeSync, tenantId, skipLoad]);

  /* Sync activeLocationId to localStorage */
  useEffect(() => {
    localStorage.setItem('activeLocationId', String(activeLocationId));
  }, [activeLocationId]);

  /* Save businessType (one-time onboarding, then only via superadmin) */
  const saveBusinessType = useCallback(async (type) => {
    setBusinessType(type);
    try {
      await apiFetch('/api/settings?key=business_type', { method: 'POST', body: { type } });
    } catch (err) {
      console.error('Failed to save business type:', err);
    }
  }, []);

  /* ── Persist to Firestore (per-group loaded refs) ── */
  // Core
  usePersistCollection('locations', locations, coreLoaded, toast);
  usePersistCollection('categories', categories, coreLoaded, toast);
  usePersistCollection('importance', importanceLevels, coreLoaded, toast);
  // customers: writes go through saveCustomer / deleteCustomer helpers
  // directly — no persistCollection sync needed since the state is gone.
  usePersistCollection('staff', staff, coreLoaded, toast);
  usePersistCollection('service_groups', serviceGroups, coreLoaded, toast);
  usePersistCollection('services', services, coreLoaded, toast);
  usePersistCollection('tariff_plans', tariffPlans, coreLoaded, toast);
  // Calendar
  usePersistCollection('appointments', appointments, calendarLoaded, toast);
  usePersistCollection('schedule_templates', scheduleTemplates, calendarLoaded, toast);
  // Finance
  usePersistCollection('transactions', transactions, financeLoaded, toast);
  // cash_shifts writes go via saveDoc directly from CashRegisterPage.
  usePersistCollection('fixed_expenses', fixedExpenses, financeLoaded, toast);
  // Inventory
  usePersistCollection('product_categories', productCategories, inventoryLoaded, toast);
  usePersistCollection('products', products, inventoryLoaded, toast);
  usePersistCollection('warehouses', warehouses, inventoryLoaded, toast);
  // stock is NOT persisted — it's derived from stock_movements on the server.
  // Local setStock(...) updates are optimistic only; canonical value comes
  // back via /api/analytics/stock-index on the next _meta/sync refetch.
  // stock_movements writes go through saveStockMovements() / server
  // endpoints, not diff-based persist. No global array to diff against.
  usePersistCollection('suppliers', suppliers, inventoryLoaded, toast);
  // Salary
  usePersistCollection('salary_schemes', salarySchemes, salaryLoaded, toast);
  // salary_history writes go via /api/collection directly from
  // SalaryCalcPage (see saveSalary handler) — no global state to persist.
  // Orders
  usePersistCollection('orders', orders, ordersLoaded, toast);
  // Production
  usePersistCollection('tech_cards', techCards, productionLoaded, toast);
  // productions writes go via saveDoc directly from ProductionPage.
  // Documents: customer_documents writes go via saveDoc/removeDoc
  // directly from CustomerModal / AppointmentModal — no global state
  // to persist here.
  // Loyalty
  usePersistCollection('loyalty_programs', loyaltyPrograms, loyaltyLoaded, toast);
  // loyalty_balances is NOT persisted — it's derived from loyalty_transactions
  // on the server. Local setLoyaltyBalances(...) updates are optimistic only;
  // canonical value comes back via /api/analytics/loyalty-balances-index on
  // the next _meta/sync refetch (bumped when a tx is written).
  // loyalty_transactions writes go directly through server endpoints, so no
  // diff-based persist needed here.
  usePersistCollection('coupons', coupons, loyaltyLoaded, toast);
  // coupon_usages writes go via saveDoc directly from POS/Orders/Calendar.
  usePersistCollection('subscription_plans', subscriptionPlans, loyaltyLoaded, toast);
  // customer_subscriptions writes go via saveDoc / /api/subscriptions/purchase
  // directly from CustomerModal / LoyaltyPage / CalendarPage.

  // Memoize context value so consumers re-render only when their slice
  // actually changes. Without this, every refetch (realtime sync every
  // 5–10 min) creates a fresh value object → all useAppData() consumers
  // re-render even if they only read unchanged fields. setX/useCallback
  // refs are already stable, so deps list is the state vars only.
  const value = useMemo(() => ({
    loading, groupsReady, ensureGroups, readOnly,
    lockCollection, unlockCollection,
    businessType, businessTypeLoaded, saveBusinessType,
    planModules,
    locations, setLocations,
    activeLocationId, setActiveLocationId,
    categories, setCategories,
    importanceLevels, setImportanceLevels,
    staff, setStaff,
    serviceGroups, setServiceGroups,
    services, setServices,
    appointments, setAppointments,
    transactions, setTransactions,
    scheduleTemplates, setScheduleTemplates,
    productCategories, setProductCategories,
    products, setProducts,
    warehouses, setWarehouses,
    stock, setStock,
    suppliers, setSuppliers,
    salarySchemes, setSalarySchemes,
    fixedExpenses, setFixedExpenses,
    tariffPlans, setTariffPlans,
    tasksOverdue, setTasksOverdue,
    tasksBlockedByMe, setTasksBlockedByMe,
    notificationsUnread, setNotificationsUnread,
    orders, setOrders,
    techCards, setTechCards,
    loyaltyPrograms, setLoyaltyPrograms,
    loyaltyBalances, setLoyaltyBalances,
    coupons, setCoupons,
    subscriptionPlans, setSubscriptionPlans,
    unreadMsgCount, setUnreadMsgCount,
    subscribeSync,
    historyWindowDays,
    ensureHistoryWindow,
  }), [
    loading, groupsReady, ensureGroups, readOnly,
    lockCollection, unlockCollection,
    businessType, businessTypeLoaded, saveBusinessType,
    planModules,
    locations, activeLocationId,
    categories, importanceLevels,
    staff, serviceGroups, services,
    appointments, transactions, scheduleTemplates,
    productCategories, products, warehouses, stock, suppliers,
    salarySchemes, fixedExpenses, tariffPlans,
    orders, techCards,
    loyaltyPrograms, loyaltyBalances, coupons, subscriptionPlans,
    unreadMsgCount, subscribeSync,
    historyWindowDays, ensureHistoryWindow,
    // Лічильники правлять на місці після дії (див. TasksPage.patchTask).
    // Без них у залежностях значення контексту не перераховувалось, і
    // бейдж показував старе число, доки не перемалюється щось інше.
    tasksOverdue, tasksBlockedByMe, notificationsUnread,
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export function useAppData() {
  return useContext(DataContext);
}
